import { useCallback, useContext, useEffect, useRef, useState } from "react";

import EcheckinDao from "@dao/EcheckinDao";
import StudentInfoDao from "@dao/StudentInfoDao";

import { DASH_LINKS } from "@const/AppCardLinks";
import { ECHECKIN_EXCLUDED_SCHOOLS } from "@const/EcheckinConst";
import { formatDate, getEpoch, getTodayWithTime } from "@utils/DateFormatter";
import { getAttributeIds } from "@const/StudentInfoConst";
import { GlobalContext } from "@components/contextProvider/ContextProvider";
import type { AppObject } from "@/types/GlobalState";

// ─── Types ────────────────────────────────────────────────────────────────────

interface LocationDto {
    key: string | number;
    feederLocationId?: string | number | null;
    icId?: (string | number)[];
}

interface SchoolYearDto {
    key: string | number;
}

interface GuardianMap {
    key: string | number;
    primSecFlag: string;
    [key: string]: unknown;
}

interface CustomAttribute {
    element: string;
    objectName: string;
    value: string;
    createdDate?: string;
}

type IdField = string | number | undefined;

interface EcheckinStudent {
    key?: string;
    studentNumber?: string | number;
    locationKey?: string | number;
    schoolId?: string | number;
    feederLocationId?: string | number | null;
}

// ─── Module-level helpers ────────────────────────────────────────────────────

const getLocationDto = (
    allLocationDtos: LocationDto[],
    schoolId: IdField,
    locationKey: IdField,
    _studentNumber: IdField
): LocationDto | null => {
    try {
        let locMatch: LocationDto[] = [];
        if (schoolId) {
            locMatch = allLocationDtos.reduce<LocationDto[]>((result, obj) => {
                const schoolMatch = obj.icId?.filter(
                    (item) => Number.parseInt(String(item), 10) === Number.parseInt(String(schoolId), 10)
                );
                if (schoolMatch && schoolMatch.length > 0) result.push(obj);
                return result;
            }, []);
        } else if (locationKey) {
            locMatch = allLocationDtos.filter(
                (obj) => Number.parseInt(String(obj.key), 10) === Number.parseInt(String(locationKey), 10)
            );
        }
        if (locMatch.length > 0 && !ECHECKIN_EXCLUDED_SCHOOLS.includes(Number.parseInt(String(locMatch[0].key), 10))) {
            return locMatch[0];
        }
    } catch (error: unknown) {
        console.error("Unable to retrieve a Location for Student", error);
    }
    return null;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Custom hook to determine if a student's guardian has access to the ECheck-In app tile
 * @name useEcheckinApp
 * @param {EcheckinStudent} student
 * @param {boolean} enabled
 * @returns {{ appObj: AppObject | null, loading: boolean }}
 */
export const useEcheckinApp = (
    student: EcheckinStudent,
    enabled = true
): { appObj: AppObject | null; loading: boolean } => {
    const context = useContext(GlobalContext);
    const { dispatch, state } = context ?? {};
    const { allLocationDtos, schoolYearDto, studentAppObjects, username } =
        (state as {
            allLocationDtos?: LocationDto[];
            schoolYearDto?: SchoolYearDto;
            studentAppObjects?: Record<string, Record<string, { appObj: AppObject | null }>>;
            username?: string | null;
        }) || {};

    const { key: studentKey, studentNumber, locationKey, schoolId, feederLocationId } = student || {};

    const [appObj, setAppObj] = useState<AppObject | null>(null);
    const [loading, setLoading] = useState(true);

    const existing = studentKey ? studentAppObjects?.[studentKey]?.ECHECKIN : null;

    const studentAppObjectsRef = useRef(studentAppObjects);
    useEffect(() => {
        studentAppObjectsRef.current = studentAppObjects;
    }, [studentAppObjects]);

    const dispatchAppObject = useCallback(
        (obj: AppObject | null) => {
            const currentAppObjects = studentAppObjectsRef.current;
            const payload = {
                ...currentAppObjects,
                [studentKey!]: {
                    ...currentAppObjects?.[studentKey!],
                    ECHECKIN: { appObj: obj }
                }
            };
            dispatch?.({ type: "StudentAppObjects", studentAppObjects: payload });
        },
        [dispatch, studentKey]
    );

    useEffect(() => {
        if (existing) {
            setAppObj(existing.appObj);
            setLoading(false);
        }
    }, [existing]);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        if (!enabled) {
            setLoading(false);
            return;
        }
        if (existing) return;
        if (!allLocationDtos || !schoolYearDto || !studentNumber) {
            setAppObj(null);
            setLoading(false);
            return;
        }

        const fetchCustomAttribute = async (app: AppObject, map: GuardianMap): Promise<AppObject> => {
            try {
                const attributes = getAttributeIds(map.primSecFlag);
                if (!attributes) return app;

                const attributeIds = Object.keys(attributes).join();
                const response = await StudentInfoDao<{ payload: CustomAttribute[] }>({
                    action: "customAttributesRead",
                    params: { attributeIds, checkForActiveEnrollment: true },
                    retrieveAllFlag: true,
                    studentNumber,
                    signal
                });
                if (response?.data?.payload) {
                    const ca = map.primSecFlag === "PRIMARY" ? "check_online" : "check_online_sec";
                    const checkinMatch = response.data.payload.find(
                        (attr) => attr.element === ca && attr.objectName === "Enrollment"
                    );
                    if (checkinMatch && Number.parseInt(checkinMatch.value, 10) === 1) {
                        return {
                            ...app,
                            status: "Global_Completed",
                            statusColor: "#4a8319",
                            subStatus: "Global_Completed",
                            subStatusRange: formatDate(checkinMatch.createdDate ?? null, true, true) || null
                        };
                    }
                }
                return app;
            } catch (error: unknown) {
                if ((error as Error).name !== "AbortError") {
                    console.error("Unable to retrieve Custom Attributes for Student", error);
                }
                return app;
            }
        };

        const fetchEcheckin = async (locationDto: LocationDto): Promise<AppObject | null> => {
            try {
                const response = await EcheckinDao<{
                    payload: {
                        checkinEndDate: string;
                        checkinStartDate: string;
                        postCheckinMessage: string;
                        preCheckinMessage: string;
                    };
                }>({
                    action: "checkinWindowParentCheckin",
                    locKey: locationDto.key,
                    params: { feederLocationId: locationDto.feederLocationId },
                    schoolYearKey: schoolYearDto.key,
                    signal
                });

                if (!response?.data?.payload) return null;

                const { checkinEndDate, checkinStartDate, postCheckinMessage, preCheckinMessage } =
                    response.data.payload;
                const todayEpoch = getEpoch(getTodayWithTime());
                const startEpoch = getEpoch(checkinStartDate);
                const endEpoch = getEpoch(checkinEndDate);

                if (todayEpoch === null || startEpoch === null || endEpoch === null) return null;

                const isOpen = todayEpoch >= startEpoch && todayEpoch < endEpoch;
                let notice: string[] | null = null;
                if (todayEpoch < startEpoch) {
                    notice = [preCheckinMessage];
                } else if (todayEpoch >= endEpoch) {
                    notice = [postCheckinMessage];
                }

                return {
                    app: "ECHECKIN",
                    body: "ECApp_Body",
                    buttons: isOpen
                        ? [
                              {
                                  label: "ECApp_Btn1",
                                  href: studentNumber ? `${DASH_LINKS.echeckin}/${studentNumber}` : null
                              }
                          ]
                        : null,
                    endDate: formatDate(checkinEndDate, true, true),
                    icon: "EXPRESS_CHECKIN_ICON",
                    moreInfo: null,
                    notice,
                    startDate: formatDate(checkinStartDate, true, true),
                    status: "Global_NotStarted",
                    statusColor: "#707070",
                    statusNumber: null,
                    subBody: null,
                    subStatus: null,
                    subStatusRange: null,
                    title: "ECApp_Header"
                };
            } catch (error: unknown) {
                if ((error as Error).name !== "AbortError") {
                    console.error("Unable to retrieve Parent Checkin Window for School Year", error);
                }
                return null;
            }
        };

        const fetchGuardianMap = async (locationDto: LocationDto): Promise<GuardianMap | null> => {
            try {
                const response = await EcheckinDao<{ payload: GuardianMap | null }>({
                    action: "guardianStudentMapByStudentNumberRead",
                    params: { locKey: locationDto.key },
                    studentNumber,
                    username,
                    signal
                });
                return response?.data?.payload ?? null;
            } catch (error: unknown) {
                if ((error as Error).name !== "AbortError") {
                    console.error("Unable to retrieve Guardian Student Map for Student", error);
                }
                return null;
            }
        };

        const fetchSignature = async (app: AppObject, map: GuardianMap): Promise<AppObject> => {
            try {
                const response = await EcheckinDao<{ payload: unknown }>({
                    action: "eSignatureRead",
                    mapId: map.key,
                    type: "GUARDIAN_AFFIRM",
                    signal
                });
                if (response?.data?.payload) {
                    return {
                        ...app,
                        body: "ECApp_Body",
                        status: "Global_InProgress",
                        statusColor: "#0669B3"
                    };
                }
                return app;
            } catch (error: unknown) {
                if ((error as Error).name !== "AbortError") {
                    console.error("Unable to retrieve Guardian Affirmation for Student", error);
                }
                return app;
            }
        };

        const applyMapUpdates = async (app: AppObject, map: GuardianMap): Promise<AppObject> => {
            const withSignature = await fetchSignature(app, map);
            return fetchCustomAttribute(withSignature, map);
        };

        const run = async (): Promise<void> => {
            try {
                setLoading(true);
                const locationDto = getLocationDto(allLocationDtos, schoolId, locationKey, studentNumber);
                if (!locationDto) {
                    setLoading(false);
                    return;
                }

                // Parallelize independent calls
                const [app, map] = await Promise.all([fetchEcheckin(locationDto), fetchGuardianMap(locationDto)]);

                if (!app) {
                    dispatchAppObject(null);
                    return;
                }

                const finalAppObj = map ? await applyMapUpdates(app, map) : app;
                if (!signal.aborted) {
                    setAppObj(finalAppObj);
                    dispatchAppObject(finalAppObj);
                }
            } catch (error: unknown) {
                if ((error as Error).name !== "AbortError") {
                    console.error("Unable to retrieve Express Check-In App", error);
                }
            } finally {
                if (!signal.aborted) {
                    setLoading(false);
                }
            }
        };

        void run();

        return (): void => {
            controller.abort();
        };
    }, [
        allLocationDtos,
        dispatchAppObject,
        enabled,
        existing,
        feederLocationId,
        locationKey,
        schoolId,
        schoolYearDto,
        studentNumber,
        username
    ]);

    return { appObj, loading };
};
